// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * I-9 processing-status badge, shared by the document table, the rerun tracker, and
 * the run-history dialog so a given status always looks the same everywhere.
 */
import type { FC } from 'react'
import { AlertTriangle } from 'lucide-react'
import { formatTimestamp } from './format'

const STATUS_COLORS: Record<string, string> = {
  failed: 'bg-red-100 text-red-800',
  matched: 'bg-green-100 text-green-800',
  partial_match: 'bg-amber-100 text-amber-800',
  no_match: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  awaiting_retry: 'bg-orange-100 text-orange-800',
  new: 'bg-blue-100 text-blue-800',
  shredded: 'bg-purple-100 text-purple-800',
  purged: 'bg-slate-200 text-slate-600',
}

export function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-800'
}

export const StatusBadge: FC<{ status: string }> = ({ status }) => (
  <span className={`px-2 py-1 rounded text-xs ${statusColor(status)}`}>{status}</span>
)

export interface OperationalError {
  code: string
  error: string
  at: string
}

/**
 * Amber warning shown beside the status badge when a side operation failed without
 * changing the document's status — a staging error, a results-archive failure, a
 * rematch skip, or a failed Optix metadata write-back. The backend clears
 * operationalError on forward progress, so a visible warning is always outstanding.
 */
export const OperationalWarning: FC<{
  operationalError?: OperationalError | null
  optixWriteStatus?: string | null
  optixDeleteStatus?: string | null
  optixDeleteError?: string | null
}> = ({ operationalError, optixWriteStatus, optixDeleteStatus, optixDeleteError }) => {
  const warnings: string[] = []
  const info: string[] = []
  if (operationalError) {
    warnings.push(
      `${operationalError.code}: ${operationalError.error} (${formatTimestamp(operationalError.at)})`
    )
  }
  if (optixWriteStatus === 'failed') {
    warnings.push('Optix metadata write-back failed')
  }
  if (optixDeleteStatus === 'pending') {
    info.push('Optix delete in progress')
  }
  if (optixDeleteStatus === 'failed') {
    warnings.push(
      optixDeleteError
        ? `Optix delete failed: ${optixDeleteError}`
        : 'Optix delete failed — purge again to retry'
    )
  }
  const lines = [...warnings, ...info]
  if (lines.length === 0) return null
  const label = lines.length === 1 ? lines[0] : `${lines.length} warnings`
  const isWarning = warnings.length > 0
  const classes = isWarning
    ? 'bg-amber-50 text-amber-800'
    : 'bg-blue-50 text-blue-800'
  const iconClass = isWarning ? 'text-amber-500' : 'text-blue-500'
  return (
    <span
      title={lines.join('\n')}
      data-testid="i9-operational-warning"
      className={`inline-flex max-w-96 items-start gap-1 rounded px-1.5 py-0.5 text-xs ${classes}`}
    >
      <AlertTriangle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconClass}`} />
      <span className="whitespace-normal break-words">{label}</span>
    </span>
  )
}
